/**
 * «Tu inglés» — la VARIEDAD de inglés que aprende el alumno (EE. UU., Reino
 * Unido o Australia). Fuente única: `conversationPrefs.region`. De ella salen,
 * desde el encargo 111 (2026-10-16):
 *   · la orden de variedad del coach (`<target_dialect>` + el bloque de léxico);
 *   · la referencia de pronunciación (charla y «Decila»);
 *   · cómo escribe el corrector sus `fix`;
 *   · el coach por defecto (ver {@link pickDefaultCoachForRegion}).
 *
 * Antes la voz del coach (`personas.accent`) decidía el dialecto y «Variedad»
 * iba aparte: un coach de EE. UU. con «Variedad UK» recibía dos órdenes
 * opuestas. Ahora la voz SIGUE a la variedad: elegir un coach de otro acento
 * cambia «Tu inglés» con él (lo hace el cliente en el mismo gesto).
 */
import type { ConvRegion } from './conversation-prefs.js';

/** Acento de persona (`personas.accent`) de cada variedad. */
export type EnglishAccent = 'US' | 'UK' | 'AU';

export const CONV_REGION_ACCENT: Readonly<Record<ConvRegion, EnglishAccent>> = {
  american: 'US',
  british: 'UK',
  australian: 'AU',
};

/** BCP-47 del dialecto de cada variedad (prompt del coach y PA). */
export type EnglishDialect = 'en-US' | 'en-GB' | 'en-AU';

export const CONV_REGION_DIALECT: Readonly<Record<ConvRegion, EnglishDialect>> = {
  american: 'en-US',
  british: 'en-GB',
  australian: 'en-AU',
};

/**
 * Acento normalizado de una persona (`US` / `UK`|`GB` / `AU`). `null` = la
 * persona no es de una variedad de inglés (personas de español, custom sin
 * acento): no mueve «Tu inglés».
 */
export function normalizeEnglishAccent(accent: string | null | undefined): EnglishAccent | null {
  const a = (accent ?? '').toUpperCase();
  if (a === 'US') return 'US';
  if (a === 'UK' || a === 'GB') return 'UK';
  if (a === 'AU') return 'AU';
  return null;
}

/** Variedad que corresponde al acento de un coach; `null` si no es de inglés. */
export function regionForAccent(accent: string | null | undefined): ConvRegion | null {
  const a = normalizeEnglishAccent(accent);
  if (a === 'US') return 'american';
  if (a === 'UK') return 'british';
  if (a === 'AU') return 'australian';
  return null;
}

/**
 * Coach por defecto de una variedad, entre `candidates` (las personas del
 * provider TTS activo, en el orden del catálogo: `display_order`). La misma
 * regla corre en el backend (sesión, audio) y en el cliente (lo que muestra
 * el selector), así los dos nunca difieren:
 *   1. el default configurado del módulo (`configuredDefaultVoiceId`, speak:
 *      `module_config.languages.geminiTtsVoice`) si es de ESA variedad;
 *   2. si no, el primero del catálogo con ese acento.
 * `null` = el provider no tiene ningún coach de esa variedad (el caller decide
 * si es un error de configuración).
 */
export function pickDefaultCoachForRegion<T extends { accent: string | null; voiceId: string }>(
  candidates: ReadonlyArray<T>,
  configuredDefaultVoiceId: string | null,
  region: ConvRegion,
): T | null {
  const accent = CONV_REGION_ACCENT[region];
  const configured =
    configuredDefaultVoiceId === null
      ? undefined
      : candidates.find((c) => c.voiceId === configuredDefaultVoiceId);
  if (configured && normalizeEnglishAccent(configured.accent) === accent) return configured;
  return candidates.find((c) => normalizeEnglishAccent(c.accent) === accent) ?? null;
}
